import { useMutation } from "@tanstack/react-query";
import { calculateBookingPrice } from "api/booking.api";
import { useSelectedTimings } from "context/TimingContext";
import { useToast } from "hooks";
import { DiscountTooltip, HeaderPaymentDetailsCard, TotalCostTooltip, } from "pages/user/components";
import { userRoute } from "pages/user/user.routes";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ButtonPrimary } from "ui/base/Button/ButtonStyled";
import { DateCard } from "ui/base/dateCard/DateCard";
import { LoaderComponent } from "ui/base/loading/LoaderComponent";
import { CloseIcon } from "ui/icons/inputIcons/CloseIcon";
import { defaultThemeColors, fonts, mediaSizes, themeColor } from "ui/theme";
import { scrollbarStyles } from "ui/base/scrollbarStyle/scrollbarStyle";
import { USDollar } from "utils";
import { motion, AnimatePresence } from 'framer-motion';

export const SelectedDates = ({ classDetail, isLoading }) => {
    const navigate = useNavigate();
    const {
        numberOfStudents,
        selectedTimings,
        handleRemoveSelectedTiming,
        typeOfClass,
    } = useSelectedTimings();

    const [lessonsCost, setLessongsCost] = useState({
        classCost: 0,
        discount: 0,
        tax: 0,
        totalCost: 0,
    });

    const teacherInfo = classDetail?.teacher;

    const onlineType = teacherInfo?.online_type;
    const onlineCategory = onlineType === "both" ? "In-Person/Online" : onlineType === "offline" ? "In-Person" : "Online"

    const { mutate: getBookingPrice } = useMutation({
        mutationFn: calculateBookingPrice,
        onError: () => useToast("error", "Booking price can not load"),
        onSuccess: ({ data }) => {
            setLessongsCost({
                classCost: data.booking_total,
                discount: data.discount_amount,
                tax: data.tax,
                totalCost: data.total_cost,
            });
        },
    });

    useEffect(() => {
        if (!selectedTimings.length) return;

        getBookingPrice({
            online_type: selectedTimings[0].type?.toLowerCase(),
            class_type: typeOfClass.value.toLowerCase(),
            no_of_class: selectedTimings.length,
            no_of_users: numberOfStudents.value,
            teacher_id: teacherInfo.id,
        });
    }, [selectedTimings.length, numberOfStudents.value]);

    return (
        <DetailsCard>
            {isLoading ? <LoaderComponent /> : <>
                <HeaderPaymentDetailsCard
                    profilePic={teacherInfo?.profile_pic}
                    title={teacherInfo?.subject_sub_category}
                    rate={teacherInfo?.rate}
                    review={teacherInfo?.review}
                    classType={teacherInfo?.type_of_class}
                    onlineType={onlineCategory}
                    levelOfStudent={teacherInfo?.level_of_student?.replaceAll?.(
                        ",",
                        ", "
                    )}
                />

                {!!teacherInfo?.teacher_discount_policy.length ? (
                    <DiscountTooltip discountList={teacherInfo?.teacher_discount_policy} />
                ) : null}

                <AnimatePresence initial={false}>
                    {selectedTimings?.length > 0 && (
                        <ContentWrapper
                            as={motion.div}
                            key="contentWrapper"
                            variants={{
                                hidden: { height: 0, opacity: 0, overflow: 'hidden' },
                                visible: { height: 'auto', opacity: 1, overflow: 'hidden' },
                                exit: { height: 0, opacity: 0, overflow: 'hidden' },
                            }}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            transition={{ duration: 0.2, ease: 'easeInOut' }}
                        >
                            <ScheduleHeader>
                                <HeaderLabel>TYPE</HeaderLabel>
                                <HeaderLabel>DATE</HeaderLabel>
                                <HeaderLabel>TIME</HeaderLabel>
                                <HeaderLabel>COST</HeaderLabel>
                                <HeaderLabel></HeaderLabel>
                            </ScheduleHeader>

                            <CardsContainer>
                                {selectedTimings.map(({ date, time, cost, id, type }, index) => (
                                    <DateCard
                                        key={index}
                                        hideTitle
                                        date={{ date, time, cost, type }}
                                        action={
                                            <StyledCloseIcon
                                                onClick={() => handleRemoveSelectedTiming({ id, date })}
                                            />
                                        }
                                        isSelected
                                    />
                                ))}
                            </CardsContainer>

                            <Summary>
                                <InfoContainer>
                                    <span>{numberOfStudents.label}</span>
                                    {lessonsCost.discount === 0 ? null : <DiscountText>{"Total price after discount"}</DiscountText>}
                                </InfoContainer>
                                <SummaryRow>
                                    <TotalCost>
                                        <span>Total (USD)</span>
                                        <TooltipWrapper>
                                            {!!selectedTimings.length ? USDollar.format(lessonsCost.totalCost) : "$0"}
                                            {!!selectedTimings.length ? (
                                                <TotalCostTooltip costData={lessonsCost} />
                                            ) : null}
                                        </TooltipWrapper>
                                    </TotalCost>
                                </SummaryRow>
                            </Summary>

                            <ButtonPrimary
                                onClick={() =>
                                    navigate(userRoute.paymentPage + classDetail.id, {
                                        state: { timing: selectedTimings, numberOfStudents },
                                    })
                                }
                                disabled={selectedTimings.length === 0}
                            >
                                Select Payment
                            </ButtonPrimary>
                        </ContentWrapper>
                    )}
                </AnimatePresence>
            </>}

        </DetailsCard>
    );
};

const DetailsCard = styled.div`
    width: 100%;
    height: fit-content;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    background-color: ${defaultThemeColors.white};
    border-radius: 5px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
    min-height: 0;
    max-height: fit-content;
    padding-block: 1rem;
    padding-inline: 0.5rem;
    max-width: 600px;
    order: 1;
    margin-inline: auto;
    @media (width >= ${mediaSizes.md}) {
        order: 2;
        margin-right: unset;
        padding-inline: 1rem;
    }
`;

const ContentWrapper = styled.div`
    //padding-right: 0.5rem;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow: auto;
    gap: 1rem;

    ${scrollbarStyles}
`;

const CardsContainer = styled.div`
    display: grid;
    gap: 1.2rem;
    min-height: 0;
`;

const Summary = styled.div`
    display: grid;
    gap: 0.5rem;

    & > span {
        opacity: 0.5;
    }
`;

const SummaryRow = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: ${fonts.sizes.regular};
    font-weight: 600;
    padding-bottom: 1.2rem;
    border-bottom: 1px solid rgba(25, 29, 58, 0.1);
`;

const TotalCost = styled.span`
    width: 100%;
    display: flex;
    align-items: center;
    font-size: ${fonts.sizes.big}; 
    font-weight: bold;
    color: ${defaultThemeColors.txt};
`;

const TooltipWrapper = styled.span`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-left: 2rem;
`;

const StyledCloseIcon = styled(CloseIcon)`
    cursor: pointer;
    color: ${defaultThemeColors.txt};
    opacity: 0.5;
`;

const ScheduleHeader = styled.div`
    display: grid;
    grid-template-columns: repeat(5,1fr);
    align-items: center;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid ${defaultThemeColors.lightBlue};
`;

const HeaderLabel = styled.span`
    color: ${defaultThemeColors.placeholder};
    font-size: ${fonts.sizes.extraSmall};
    font-weight: 600;
    text-align: left;
`;

const InfoContainer = styled.div`
    display: flex;
    gap: 8px;
`

const DiscountText = styled.p`
    color: ${themeColor('blue')};
`